'use client';

import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CreditCard, WarningCircle, SpinnerGap } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useAuthStore, selectIsAuthenticated } from '@/stores/auth-store';
import { captureEvent } from '@/lib/source-tracker';
import {
  getUnpaidOrders,
  getOrderPaymentLink,
  selectPayableOrders,
  UNPAID_ORDER_WINDOW_HOURS,
  type OrderHistoryEntry,
} from '@/lib/api/orders';

/**
 * Неоплаченные заказы (вопрос 20, ответ «А» + «показать на главном и в профиле»).
 *
 * Сервер умеет отдавать такие заказы (`GET /orders?include_unpaid=1`) и умеет
 * пересобирать ссылку на оплату (`GET /orders/:id/payment-link`) — но ни сайт,
 * ни приложение об этом не просили, и человек с непрошедшей оплатой не видел
 * свой заказ нигде. Он оформлял второй, а первый висел мёртвым.
 *
 * Про срок. Автоотмены неоплаченных заказов на сервере НЕТ, поэтому текст
 * рядом с кнопкой не обещает, что заказ «аннулируется сам». Мы предлагаем
 * доплатить сутки (UNPAID_ORDER_WINDOW_HOURS) — дальше заказ проще оформить
 * заново по актуальным ценам и часам работы.
 */

const formatPrice = (price: number): string =>
  Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';

const formatWhen = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const orderTotal = (order: OrderHistoryEntry): number =>
  Number(order.total ?? order.total_amount ?? 0);

// ========== ОДНА КАРТОЧКА ==========

function UnpaidOrderCard({ order, compact }: { order: OrderHistoryEntry; compact?: boolean }) {
  const [state, setState] = useState<'idle' | 'loading' | 'paid' | 'error'>('idle');

  const handlePay = useCallback(async () => {
    setState('loading');
    try {
      const data = await getOrderPaymentLink(order.id);
      captureEvent('payment_retry_started', { order_id: order.id, total: orderTotal(order) });
      // Уход на платёжку, а не открытие корзины: заказ уже создан, второй
      // создавать нельзя.
      window.location.href = data.paymentUrl;
    } catch (error) {
      // 409 — оплата всё-таки прошла (callback дошёл позже).
      const status = (error as { response?: { status?: number } })?.response?.status;
      setState(status === 409 ? 'paid' : 'error');
    }
  }, [order]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'rounded-2xl border border-[var(--warning)]/30 bg-[var(--warning-bg)]',
        compact ? 'p-3' : 'p-4'
      )}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-[var(--warning)]/15 flex items-center justify-center flex-shrink-0">
          <WarningCircle weight="fill" className="w-5 h-5 text-[var(--warning)]" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[var(--text-primary)]">
            Заказ #{order.id} ждёт оплаты
          </p>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">
            {formatWhen(order.created_at)} · {formatPrice(orderTotal(order))}
          </p>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">
            {state === 'paid'
              ? 'Оплата подтвердилась — платить повторно не нужно, обновите страницу.'
              : state === 'error'
                ? 'Ссылку на оплату сейчас не собрать. Позвоните нам или оформите заказ заново.'
                : `Деньги не списаны, заказ мы не готовим. Доплатить можно в течение ${UNPAID_ORDER_WINDOW_HOURS} часов после оформления — дальше оформите заново.`}
          </p>
        </div>
      </div>

      {state !== 'paid' && (
        <button
          type="button"
          onClick={handlePay}
          disabled={state === 'loading'}
          className={cn(
            'mt-3 w-full h-11 rounded-xl font-semibold text-sm flex items-center justify-center gap-2',
            'bg-[var(--primary)] text-white transition-colors',
            'hover:bg-[var(--primary-hover)] disabled:opacity-60'
          )}
        >
          {state === 'loading' ? (
            <SpinnerGap weight="bold" className="w-4 h-4 animate-spin" />
          ) : (
            <CreditCard weight="fill" className="w-4 h-4" />
          )}
          {state === 'loading' ? 'Открываем оплату…' : 'Оплатить'}
        </button>
      )}
    </motion.div>
  );
}

// ========== БЛОК В ПРОФИЛЕ ==========

/** Готовый список — данные уже загружены страницей профиля. */
export function UnpaidOrdersList({
  orders,
  compact,
}: {
  orders: OrderHistoryEntry[];
  compact?: boolean;
}) {
  if (orders.length === 0) return null;

  return (
    <div className="space-y-3">
      <h3 className="text-base font-bold text-[var(--text-primary)]">Ожидает оплаты</h3>
      {orders.map((order) => (
        <UnpaidOrderCard key={order.id} order={order} compact={compact} />
      ))}
    </div>
  );
}

// ========== БАННЕР НА ГЛАВНОЙ ==========

/**
 * Самостоятельный баннер для главной: сам ходит за заказами, но только у
 * авторизованных — гостю показывать нечего, а лишний запрос на главной дорог.
 */
export function UnpaidOrderBanner() {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);

  const { data } = useQuery({
    queryKey: ['orders', 'unpaid'],
    // Без состава заказов: на главной он не нужен, а стоит запроса на каждый
    // заказ. См. getUnpaidOrders.
    queryFn: getUnpaidOrders,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  const payable = data ? selectPayableOrders(data) : [];
  if (payable.length === 0) return null;

  // Показываем только самый свежий: баннер на главной — напоминание, а не
  // история. Остальные видно в профиле.
  return (
    <div className="mb-4 mr-4 sm:mr-0">
      <UnpaidOrderCard order={payable[0]} />
    </div>
  );
}
